import React, { useMemo, useRef } from 'react';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import { useToday } from 'shared/contexts/today';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useTaskPlannedOccurrenceForDate } from 'shared/hooks/use-task-planned-occurrence-for-date';
import { useUser } from 'shared/hooks/use-user';
import { DataType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { getScheduleByDate } from 'shared/utils/schedules';
import { useTheme } from 'styled-components';

export type ContextMenuProps = {
  children: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  task: Task;
  hideMeta?: boolean;
  timeslotDate?: Timestamp;
  externalPosition?: { left: number; top: number };
};

export const ContextMenu: React.FC<ContextMenuProps> = ({
  children,
  task,
  onOpen,
  onClose,
  hideMeta,
  timeslotDate,
  externalPosition,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const { position, open, close } = useContextMenuState({
    disabled: theme.isMobile || !!task.frozenAt,
    onOpen: () => onOpen?.(),
    onClose: () => onClose?.(),
  });

  const activeSchedule = useMemo(
    () =>
      task.schedules
        ? timeslotDate
          ? getScheduleByDate(task.schedules, timeslotDate)
          : getActiveSchedule(task.schedules)
        : undefined,
    [task.schedules, timeslotDate],
  );
  const plannedOccurrence = useTaskPlannedOccurrenceForDate(
    task,
    timeslotDate ?? today,
    { weekStartsOn, today },
  );

  return (
    <div
      ref={containerRef}
      data-component-type={DataType.Task}
      data-id={task.id}
      data-title={task.name}
      data-duration={
        activeSchedule?.timeslot?.duration ?? task.timeslot?.duration
      }
      onContextMenu={open}
    >
      {children}
      {(!!position || !!externalPosition) && (
        <TaskCardContextMenu
          task={task}
          onClose={close}
          location={position ?? externalPosition}
          hideMeta={hideMeta}
          timeslotDate={plannedOccurrence?.date ?? timeslotDate}
        />
      )}
    </div>
  );
};
